/**
 * Retirement Sensitivity Analysis
 *
 * WP-RETIRE-12: Ranks the changes a user could make by how much each improves
 * their funding outcome, so the product can answer "what matters most?" rather
 * than leaving them to infer it from a grid of colours.
 *
 * Deliberately a SEPARATE module from retirementCalculations.ts (one concern
 * per file). It does not reimplement any projection maths: it perturbs the
 * inputs and calls the SAME buildProjectionGrid the on-screen grid uses, so a
 * recommendation can never contradict the numbers displayed above it.
 *
 * All money is integer cents.
 */
import {
  MAX_SOCIAL_SECURITY_START_AGE,
  buildProjectionGrid,
  effectiveSocialSecurityStartAge,
} from './retirementCalculations';
import type {
  RetirementPlanningInputs,
  RetirementEvent,
} from '@/types/retirement';

/**
 * Levers a user can actually act on.
 *
 * `currentAge` is deliberately absent: it is not actionable, and ranking it
 * would put an impossible action at the top of the list.
 * `netWorthAdjustmentCents` is absent too - it is an accounting correction,
 * not a behavioural change.
 */
export type LeverId =
  | 'retirementAge'
  | 'currentSavingsAnnualCents'
  | 'retirementExpensesAnnualCents'
  | 'socialSecurityStartAge';

export interface LeverDefinition {
  id: LeverId;
  /** Short human label, e.g. "Retire one year later" */
  label: string;
  /** How the step is described to the user, e.g. "+1 year" */
  stepLabel: string;
  /** Applies this lever's normalized step to a copy of the inputs. */
  apply: (inputs: RetirementPlanningInputs) => RetirementPlanningInputs;
}

export interface LeverImpact {
  id: LeverId;
  label: string;
  stepLabel: string;
  /** Coverage ratio at the goal retirement age before the change */
  baselineCoverage: number;
  /** Coverage ratio after applying the lever's step */
  adjustedCoverage: number;
  /** adjustedCoverage - baselineCoverage; positive means an improvement */
  delta: number;
}

/** Proportional step applied to annual savings. */
export const SAVINGS_STEP_FRACTION = 0.1;

/** Proportional reduction applied to retirement expenses. */
export const EXPENSES_STEP_FRACTION = 0.05;

/**
 * Normalized, realistic step per lever (decision D6).
 *
 * Ranking is meaningless unless each lever is perturbed by a comparable and
 * plausible amount: comparing "+1 year of retirement age" against "+1 cent of
 * savings" would produce a nonsense ordering. Steps are proportional where the
 * natural unit depends on the user's means, and discrete where the real-world
 * decision is discrete.
 */
export function getLeverDefinitions(
  inputs: RetirementPlanningInputs
): LeverDefinition[] {
  const levers: LeverDefinition[] = [
    {
      id: 'retirementAge',
      label: 'Retire one year later',
      stepLabel: '+1 year',
      apply: (i) => ({ ...i, retirementAge: i.retirementAge + 1 }),
    },
    {
      id: 'retirementExpensesAnnualCents',
      label: 'Spend 5% less in retirement',
      stepLabel: '-5% expenses',
      apply: (i) => ({
        ...i,
        retirementExpensesAnnualCents: Math.round(
          i.retirementExpensesAnnualCents * (1 - EXPENSES_STEP_FRACTION)
        ),
      }),
    },
  ];

  // Only offer "save more" when there is a savings figure to increase. A
  // proportional step on zero would be zero, producing a lever that appears
  // to do nothing rather than one that is simply not applicable.
  if (inputs.currentSavingsAnnualCents > 0) {
    levers.push({
      id: 'currentSavingsAnnualCents',
      label: 'Save 10% more each year',
      stepLabel: '+10% savings',
      apply: (i) => ({
        ...i,
        currentSavingsAnnualCents: Math.round(
          i.currentSavingsAnnualCents * (1 + SAVINGS_STEP_FRACTION)
        ),
      }),
    });
  }

  // Deferring Social Security only applies while there is room to defer and a
  // benefit to defer.
  // Measured from the age Social Security really starts, so an unset or
  // pre-62 start age is deferred from 62 - not from a younger age the
  // projection would never pay it at, which made "+1 year" change nothing.
  const ssStart = effectiveSocialSecurityStartAge(
    inputs.socialSecurityStartAge,
    inputs.retirementAge
  );
  if (
    inputs.socialSecurityAnnualCents > 0 &&
    ssStart < MAX_SOCIAL_SECURITY_START_AGE
  ) {
    levers.push({
      id: 'socialSecurityStartAge',
      label: 'Claim Social Security one year later',
      stepLabel: '+1 year',
      apply: (i) => ({
        ...i,
        socialSecurityStartAge: Math.min(
          effectiveSocialSecurityStartAge(
            i.socialSecurityStartAge,
            i.retirementAge
          ) + 1,
          MAX_SOCIAL_SECURITY_START_AGE
        ),
      }),
    });
  }

  return levers;
}

/**
 * Coverage ratio at the user's goal retirement age, averaged across the three
 * return-rate scenarios.
 *
 * Averaging keeps the ranking from being an artefact of whichever single
 * scenario was picked, while preserving the three-scenario model the user
 * explicitly asked to keep simple.
 *
 * Returns 0 when the goal age is not represented in the grid (for example a
 * retirement age below the first age milestone), so a lever cannot be ranked
 * on a value that was never computed.
 */
export function coverageAtGoalAge(
  inputs: RetirementPlanningInputs,
  netWorthCents: number,
  events?: RetirementEvent[],
  birthYear?: number
): number {
  const grid = buildProjectionGrid(inputs, netWorthCents, events, birthYear);

  // Nearest age column at or after the goal retirement age
  const goalIndex = grid.ageColumns.findIndex(
    (age) => age >= inputs.retirementAge
  );
  if (goalIndex === -1) return 0;

  const ratios: number[] = [];
  for (const row of grid.cells) {
    const cell = row[goalIndex];
    if (cell) ratios.push(cell.coverageRatio);
  }
  if (ratios.length === 0) return 0;

  return ratios.reduce((sum, r) => sum + r, 0) / ratios.length;
}

/**
 * Rank the available levers by their effect on funding at the goal age.
 *
 * Sorted by absolute impact, largest first, so the caller can present the most
 * consequential changes without re-sorting.
 */
export function rankLevers(
  inputs: RetirementPlanningInputs,
  netWorthCents: number,
  events?: RetirementEvent[],
  birthYear?: number
): LeverImpact[] {
  const baseline = coverageAtGoalAge(inputs, netWorthCents, events, birthYear);

  return getLeverDefinitions(inputs)
    .map((lever) => {
      const adjusted = coverageAtGoalAge(
        lever.apply(inputs),
        netWorthCents,
        events,
        birthYear
      );
      return {
        id: lever.id,
        label: lever.label,
        stepLabel: lever.stepLabel,
        baselineCoverage: baseline,
        adjustedCoverage: adjusted,
        delta: adjusted - baseline,
      };
    })
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
}
